import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  LoadingSpinner,
  useToast,
} from '@ais-ui/ui-core';
import {
  Bookmark,
  useGetShareGroups,
  useSaveBookmark,
  useSaveSettings,
} from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { cn, getGeolocation } from '@ais-ui/utils';
import { MapContainer, useOpenLayersContext } from '@ais-ui/react-openlayers';
import { BaseMap } from '../../Map/BaseMap';
import { toLonLat } from 'ol/proj';
import { useProfile } from '@ais-ui/profile';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { grabBookmarkInitialGroups } from '../../Groups/grabInitialGroups';

const addBookmarkFormSchema = z.object({
  name: z.string().min(1),
  latitude: z.number(),
  longitude: z.number(),
  zoom: z.number(),
  selectedGroups: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
});

type AddBookmarkFormValues = z.infer<typeof addBookmarkFormSchema>;

interface BookmarksAddFormProps {
  form: UseFormReturn<AddBookmarkFormValues>;
  isFullExtent: boolean;
  setIsFullExtent: React.Dispatch<React.SetStateAction<boolean>>;
}

interface BidirectionalFormDataOnMapProps {
  form: UseFormReturn<AddBookmarkFormValues>;
}

type CreateModeProps = {
  mode: 'create';
  existingBookmark?: undefined;
  className?: string;
};

type EditModeProps = {
  mode: 'edit';
  existingBookmark: Bookmark;
  className?: string;
};

type BookmarkCreateEditServiceProps = CreateModeProps | EditModeProps;

interface FullExtentMapProps {
  form: UseFormReturn<AddBookmarkFormValues>;
  setIsFullExtent: React.Dispatch<React.SetStateAction<boolean>>;
}

const FullExtentMap = ({ form, setIsFullExtent }: FullExtentMapProps) => {
  return (
    <div className="h-full w-full space-y-2 pb-8">
      <div className={'flex w-full justify-between'}>
        <div className="space-y-2">
          <DialogTitle>Full extent</DialogTitle>
          <DialogDescription>
            The full extent reflects what is displayed on the main map
          </DialogDescription>
        </div>

        <div className="flex space-x-2 items-center">
          <Button variant="ghost" onClick={() => setIsFullExtent(false)}>
            Minimize
          </Button>
          <InfoIconTooltip>
            The bookmark indicates the center of the map, not its extent.
            Therefore, the visible extent on the main map does not directly
            correspond to the bookmark.
          </InfoIconTooltip>
        </div>
      </div>
      <div className="h-full w-full border rounded-lg">
        <MapContainer
          center={[form.getValues('longitude'), form.getValues('latitude')]}
          zoom={form.getValues('zoom')}
        >
          <BaseMap />
          <BidirectionalFormDataOnMap form={form} />
        </MapContainer>
      </div>
    </div>
  );
};

interface MapWithAspectRatioProps {
  form: UseFormReturn<AddBookmarkFormValues>;
  setIsFullExtent: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapWithAspectRatio = ({
  form,
  setIsFullExtent,
}: MapWithAspectRatioProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mapDetails } = useSettings();
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Measure the width of the container
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Compute the aspect ratio
  const aspectRatio = useMemo(() => {
    if (mapDetails.width && mapDetails.height) {
      return mapDetails.width / mapDetails.height;
    }
  }, [mapDetails.width, mapDetails.height]);

  // Calculate the map height dynamically
  const mapHeight = useMemo(() => {
    if (aspectRatio && containerWidth) {
      return Math.round(containerWidth / aspectRatio);
    }
  }, [aspectRatio, containerWidth]);

  return (
    <div className="space-y-2">
      <div className={'flex w-full justify-end space-x-2'}>
        <Button variant="ghost" onClick={() => setIsFullExtent(true)}>
          Full extent
        </Button>
        <InfoIconTooltip>
          The bookmark indicates the center of the map, not its extent.
          Therefore, the visible extent on the main map does not directly
          correspond to the bookmark.
        </InfoIconTooltip>
      </div>
      <div
        ref={containerRef}
        className="w-full rounded-lg overflow-hidden max-h-[400px]"
        style={{ height: mapHeight }}
      >
        <MapContainer
          center={[form.getValues('longitude'), form.getValues('latitude')]}
          zoom={form.getValues('zoom')}
        >
          <BaseMap />
          <BidirectionalFormDataOnMap form={form} />
        </MapContainer>
      </div>
    </div>
  );
};

const BidirectionalFormDataOnMap = ({
  form,
}: BidirectionalFormDataOnMapProps) => {
  const { map } = useOpenLayersContext();

  const updateFormValues = useCallback(() => {
    const view = map.getView();
    const center = view.getCenter();
    const zoom = view.getZoom();

    if (center && zoom !== undefined) {
      const [longitude, latitude] = toLonLat(center);

      form.setValue('latitude', parseFloat(latitude.toFixed(4)));
      form.setValue('longitude', parseFloat(longitude.toFixed(4)));
      form.setValue('zoom', zoom);
    }
  }, [map, form]);

  // Effect to listen to map changes
  useEffect(() => {
    map.on('moveend', updateFormValues);
    return () => {
      map.un('moveend', updateFormValues);
    };
  }, [map, updateFormValues]);

  return null;
};

const BookmarksAddForm = ({
  form,
  isFullExtent,
  setIsFullExtent,
}: BookmarksAddFormProps) => {
  const { messages } = useLocalization();

  if (isFullExtent) {
    return (
      <FullExtentMap
        form={form}
        setIsFullExtent={setIsFullExtent}
      ></FullExtentMap>
    );
  }

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {messages.settingsBookmarksAddDialogNameLabel} *
            </FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                {...field}
                type="text"
                placeholder={messages.settingsBookmarksAddDialogNamePlaceholder}
                autoComplete="off"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex space-x-4">
        <FormField
          control={form.control}
          name="latitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.settingsBookmarksDialogLatitudeLabel} *
                </FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Input
                  type="number"
                  placeholder={
                    messages.settingsBookmarksDialogLatitudePlaceholder
                  }
                  autoComplete="off"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseFloat(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="longitude"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>
                  {messages.settingsBookmarksDialogLongitudeLabel} *
                </FormLabel>
              </div>
              <div className="flex space-x-4">
                <div className="space-y-2">
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.settingsBookmarksDialogLongitudePlaceholder
                      }
                      autoComplete="off"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </div>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Share with</FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <MapWithAspectRatio
        form={form}
        setIsFullExtent={setIsFullExtent}
      ></MapWithAspectRatio>
    </div>
  );
};

export const BookmarksAddEdit = ({
  mode,
  existingBookmark,
  className,
}: BookmarkCreateEditServiceProps) => {
  const { messages } = useLocalization();
  const { mutate: mutateBookmark } = useSaveBookmark();
  const { userId, username } = useProfile();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mapDetails } = useSettings();
  const [isFullExtent, setIsFullExtent] = useState<boolean>(false);
  const [hasLoadedInitialValues, setHasLoadedInitialValues] =
    useState<boolean>(false);

  const form = useForm<AddBookmarkFormValues>({
    resolver: zodResolver(addBookmarkFormSchema),
  });

  useEffect(() => {
    if (!hasLoadedInitialValues && !isLoadingGroups && mode === 'edit') {
      const initialValues = {
        name: existingBookmark.name,
        latitude: existingBookmark.lat,
        longitude: existingBookmark.lon,
        zoom: existingBookmark.zoomLevel,
        selectedGroups: grabBookmarkInitialGroups(existingBookmark, groups),
      };
      form.reset(initialValues);
      setHasLoadedInitialValues(true);
    }
  }, [
    existingBookmark,
    groups,
    isLoadingGroups,
    form,
    mode,
    mapDetails.center,
    mapDetails.zoom,
    hasLoadedInitialValues,
  ]);

  useEffect(() => {
    const fetchAndSetGeolocation = async () => {
      if (!hasLoadedInitialValues && mode === 'create') {
        if (mapDetails?.center && mapDetails?.zoom) {
          form.reset({
            name: '',
            latitude: toLonLat(mapDetails.center)[1],
            longitude: toLonLat(mapDetails.center)[0],
            zoom: mapDetails.zoom,
            selectedGroups: [],
          });
          setHasLoadedInitialValues(true);
          return;
        }

        try {
          const geolocation = await getGeolocation();
          form.reset({
            name: '',
            latitude: geolocation.latitude,
            longitude: geolocation.longitude,
            zoom: mapDetails?.zoom ? mapDetails.zoom : 6,
            selectedGroups: [],
          });
          setHasLoadedInitialValues(true);
        } catch (error) {
          console.error('Geolocation failed, using default values.', error);
          // Handle the error case by using fallback values
          form.reset({
            name: '',
            latitude: 9,
            longitude: 50,
            zoom: 6,
            selectedGroups: [],
          });
          setHasLoadedInitialValues(true);
        }
      }
    };

    fetchAndSetGeolocation();
  }, [
    form,
    hasLoadedInitialValues,
    mapDetails?.center,
    mapDetails?.zoom,
    mode,
  ]);

  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  async function onEditBookmark(data: AddBookmarkFormValues) {
    if (mode !== 'edit') {
      return;
    }
    setIsSubmitting(true);

    const newBookmark: Bookmark = {
      uuid: existingBookmark.uuid,
      name: data.name,
      lat: data.latitude,
      lon: data.longitude,
      note: '',
      userid: userId,
      username: username,
      acls: data.selectedGroups.map((g) => g.value),
      zoomLevel: data.zoom,
    };

    let homeBookmark = settings.homeBookmark;
    if (settings['homeBookmark']?.uuid === newBookmark.uuid) {
      homeBookmark = newBookmark;
      const settingsToSave = {
        ...settings,
        homeBookmark,
      };
      mutateSettings(settingsToSave);
    }

    mutateBookmark(newBookmark, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBookmarksSuccessEditMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBookmarksFailedEditMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  async function onAddBookmark(data: AddBookmarkFormValues) {
    if (mode !== 'create') {
      return;
    }
    setIsSubmitting(true);

    const newBookmark: Bookmark = {
      uuid: '',
      name: data.name,
      lat: data.latitude,
      lon: data.longitude,
      note: '',
      userid: userId,
      username: username,
      acls: data.selectedGroups.map((g) => g.value),
      zoomLevel: data.zoom,
    };

    // Save the updated settings
    mutateBookmark(newBookmark, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBookmarksSuccessAddMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBookmarksFailedAddMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button
          variant={mode === 'edit' ? 'secondary' : 'default'}
          disabled={mode === 'edit' && username !== existingBookmark.username}
          className={cn(mode === 'edit' && 'text-primary', className)}
        >
          {mode === 'create'
            ? messages.settingsBookmarksAddButtonText
            : messages.settingsBookmarksEditButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent
        id="add-update-service-dialog"
        className={cn(
          'p-6 lg:p-12',
          isFullExtent
            ? 'h-[95%] max-w-[90%] max-h-none'
            : 'max-w-[450px] lg:max-w-[650px]'
        )}
      >
        {!isFullExtent && (
          <DialogHeader>
            <DialogTitle>{messages.settingsBookmarkAddTitleText}</DialogTitle>
            <DialogDescription>
              {messages.settingsBookmarksAddSubtitleText}
            </DialogDescription>
          </DialogHeader>
        )}
        {!hasLoadedInitialValues && isLoadingGroups && (
          <LoadingSpinner></LoadingSpinner>
        )}
        {hasLoadedInitialValues && (
          <Form {...form}>
            <form
              onSubmit={
                mode === 'create'
                  ? form.handleSubmit(onAddBookmark)
                  : form.handleSubmit(onEditBookmark)
              }
              className="space-y-8 w-full"
            >
              <BookmarksAddForm
                form={form}
                isFullExtent={isFullExtent}
                setIsFullExtent={setIsFullExtent}
              ></BookmarksAddForm>
              {!isFullExtent && (
                <DialogFooter className="p-4">
                  <Button
                    id="bookmark-confirm-add-button"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? messages.genericSubmitting
                      : messages.genericSubmit}
                  </Button>
                </DialogFooter>
              )}
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
